import { Injectable } from '@angular/core';
import { init, compress, decompress } from '@bokuweb/zstd-wasm';

@Injectable({
  providedIn: 'root',
})
export class ZstdBokuwebService {
  private initialized = false;
  /** Served from `src/assets/zstd/zstd.wasm` → `/assets/zstd/zstd.wasm`. */
  private readonly wasmPath = '/assets/zstd/zstd.wasm';

  /* =========================
     WASM INITIALIZATION
     ========================= */

  private async ensureReady(): Promise<void> {
    if (this.initialized) return;

    // Package `exports.default` types say init(): void (Node). Browser build
    // still accepts an optional path — pass our hosted wasm URL.
    const initWasm = init as (path?: string) => Promise<void>;
    await initWasm(this.wasmPath);

    this.initialized = true;
  }

  /* =========================
     LOW-LEVEL UTILITIES
     ========================= */

  private base64ToUint8(base64: string): Uint8Array {
    const clean = base64.replace(/\s/g, '');
    const binary = atob(clean);
    const len = binary.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private uint8ToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  }

  /* =========================
     ZSTD CORE OPERATIONS
     ========================= */

  private async compressBytes(
    bytes: Uint8Array,
    level = 3
  ): Promise<Uint8Array> {
    await this.ensureReady();
    return compress(bytes, level);
  }

  private async decompressBytes(bytes: Uint8Array): Promise<Uint8Array> {
    await this.ensureReady();
    return decompress(bytes);
  }

  /* =========================
     HIGH-LEVEL PIPELINE
     ========================= */

  /**
   * Compress binary → Base64
   * Skips compression if ineffective
   */
  async compressToBase64(bytes: Uint8Array, level = 3): Promise<string> {
    const compressed = await this.compressBytes(bytes, level);

    /* if (compressed.length >= bytes.length) {
      // no benefit → return raw base64
      return this.uint8ToBase64(bytes);
    } */

    return this.uint8ToBase64(compressed);
  }

  /**
   * Decompress Base64(ZSTD) → Base64(PDF)
   * Supports two backend payload styles after decompression:
   * 1) ASCII base64 text (e.g. starts with JVBERi0)
   * 2) Raw PDF bytes (e.g. starts with %PDF-)
   */
  async decompressBase64ToBase64(base64: string): Promise<string> {
    // Already a PDF base64 → return as-is
    if (base64.startsWith('JVBERi0')) {
      return base64;
    }

    const compressedBytes = this.base64ToUint8(base64);
    const decompressedBytes = await this.decompressBytes(compressedBytes);

    // Some endpoints return decompressed bytes as plain PDF bytes.
    // In that case, convert bytes back to base64 for the frontend pipeline.
    const pdfHeader = '%PDF-';
    const maybePdfHeader = new TextDecoder('ascii').decode(
      decompressedBytes.subarray(0, Math.min(pdfHeader.length, decompressedBytes.length))
    );
    if (maybePdfHeader === pdfHeader) {
      return this.uint8ToBase64(decompressedBytes);
    }

    // Other endpoints return decompressed bytes that are ASCII base64 text.
    return new TextDecoder('utf-8').decode(decompressedBytes).trim();
  }

  /**
   * Compress File → Base64
   */
  async compressFileToBase64(file: File, level = 3): Promise<string> {
    const buffer = await file.arrayBuffer();
    return this.compressToBase64(new Uint8Array(buffer), level);
  }

  /**
   * Compress Base64 → Compressed Base64
   * Skips compression if ineffective
   */
  async compressBase64ToCompressedBase64(
    base64: string,
    level = 3
  ): Promise<{ data: string; compressed: boolean }> {
    try {
      const originalBytes = this.base64ToUint8(base64);
      const compressedBytes = await this.compressBytes(originalBytes, level);
      console.group('------------------- ZSTD Compression Debug -------------------');
      console.log('Original size:', originalBytes.length);
      console.log('Compressed size:', compressedBytes.length);
      console.log('Compressed size >= Original size:', compressedBytes.length >= originalBytes.length);
      console.log('------------------- ZSTD Compression Debug -------------------');
      console.groupEnd();
      if (compressedBytes.length >= originalBytes.length) {
        return { data: base64, compressed: false };
      }

      return {
        data: this.uint8ToBase64(compressedBytes),
        compressed: true,
      };
    } catch (e) {
      // Any WASM / runtime failure → fallback
      return { data: base64, compressed: false };
    }
  }
}
